import React, { useEffect, useMemo, useState } from "react";
import sortBy from "lodash/sortBy";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useQuery } from "@apollo/client/react";
import uniqBy from "lodash/uniqBy";

import EntitySelect from "../../../components/EntitySelect";
import useAuth from "../../../hooks/useAuth";
import { CLIENTS_AND_PROJECTS_QUERY } from "../../../constants/graphql/queries";
import ActionsModal from "./ActionsModal";
import {
  buildDuplicateProjectNameCounts,
  getProjectClientLabel,
  getProjectOptionLabel,
} from "../../../utils/projectOptionLabels";

export const getInitClientId = (clients, selected) => {
  const client = clients.find(v => v.projects.some(project => project.id === selected));
  return client ? client.id : null;
};

export const getProjects = (clients, selectedClient) => {
  let projectList;

  if (selectedClient === "null") {
    projectList = clients.flatMap(client =>
      client.projects
        .filter(project => project.active)
        .map(project => ({
          ...project,
          client: { id: client.id, name: client.name },
        }))
    );
    projectList = uniqBy(projectList, "id");
  } else {
    const selectedClientData = clients.find(client => client.id === selectedClient);
    projectList =
      selectedClientData?.projects
        .filter(project => project.active)
        .map(project => ({
          ...project,
          client: { id: selectedClientData.id, name: selectedClientData.name },
        })) || [];
  }

  return sortBy(projectList, project => project.name?.toLowerCase() || "");
};

export const getProjectsList = (clients, selectedClient, user) => {
  const projectList = getProjects(clients, selectedClient).filter(v =>
    user.projectIds.includes(v.id)
  );
  return [{ id: "all", name: "All" }, ...projectList];
};

export default function FilterNav({
  addAllOption,
  onSelectProject,
  onSelectClient,
  clientId,
  projectId,
  md = 7,
  hideActions,
}) {
  const { user } = useAuth();
  const { data: { clients = [] } = {} } = useQuery(CLIENTS_AND_PROJECTS_QUERY, {
    fetchPolicy: "cache-first",
  });
  const { settings } = user.company;

  const initClientId = clientId
    ? clients.find(client => client.id === clientId)?.id || "null"
    : getInitClientId(clients, projectId);
  const [selectedClient, setSelectedClient] = useState(initClientId);

  const clientList = sortBy(
    clients.map(({ id, name }) => ({ id, name })),
    "name"
  ).filter(v => user.clientIds.includes(v.id));
  const projectList = getProjectsList(clients, selectedClient, user);
  const duplicateNameCounts = useMemo(
    () => buildDuplicateProjectNameCounts(projectList),
    [projectList]
  );
  const showClientNameInProjectList = Boolean(settings.display_client_name_email_notification);

  const handleChangeClient = item => {
    const value = item ? item.id : null;
    setSelectedClient(value);
    if (onSelectClient) onSelectClient(value);
  };

  const handleChangeProject = projectItem => {
    onSelectProject({
      clientId: selectedClient,
      projectId: projectItem ? projectItem.id : null,
    });
  };

  useEffect(() => {
    setSelectedClient(initClientId);
  }, [initClientId]);

  return (
    <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
      <Grid container spacing={2} size={{ xs: 12, md: md }}>
        <Grid size={6}>
          <EntitySelect
            fullWidth
            label={settings.filter_field1}
            name="client"
            value={selectedClient}
            suggestions={addAllOption ? [{ id: "null", name: "All" }, ...clientList] : clientList}
            onChange={handleChangeClient}
          />
        </Grid>
        <Grid size={6}>
          <EntitySelect
            fullWidth
            disabled={!selectedClient}
            allowEmpty
            name="project"
            label={settings.filter_field2}
            value={projectId}
            suggestions={projectList}
            getOptionLabel={option =>
              getProjectOptionLabel(option, duplicateNameCounts, showClientNameInProjectList)
            }
            renderOption={(props, option) => {
              const optionProps = { ...props };
              delete optionProps.key;
              const clientLabel = getProjectClientLabel(
                option,
                duplicateNameCounts,
                showClientNameInProjectList
              );
              return (
                <li {...optionProps} key={option.id}>
                  {option.name}
                  {clientLabel ? (
                    <Typography component="span" ml={0.5} color="text.secondary" fontSize={14}>
                      {" - "}
                      {clientLabel}
                    </Typography>
                  ) : null}
                </li>
              );
            }}
            onChange={handleChangeProject}
          />
        </Grid>
      </Grid>
      {!hideActions && (
        <Grid container spacing={2} justifyContent="flex-end" size={{ xs: 12, md: 5 }}>
          <Grid size={12}>
            <ActionsModal />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
